import type { Feature, LineString, MultiLineString } from 'geojson';
import L from 'leaflet';
import { offsetCoordinates } from '../Util';
import type { RoadStatusLayerOptions, StatusData } from './Layers.types';

// The proj4leaflet CRS (L.Proj.CRS) exposes the zoom-independent proj4
// projection and the linear pixel transformation, which the base @types/leaflet
// CRS interface omits. Both are used to split projection (cached) from per-zoom
// scaling.
type ProjectingCrs = L.CRS & {
    projection: { project(latlng: L.LatLng): L.Point; unproject(point: L.Point): L.LatLng };
    transformation: { transform(point: L.Point, scale?: number): L.Point };
};

const defaultOptions: Partial<RoadStatusLayerOptions> = {
    style: {
        color: '#ccc',
        opacity: 0,
        weight: 2.5,
    },
    renderer: L.canvas({ tolerance: 5 }),
};

interface RoadStatusLayer {
    options: RoadStatusLayerOptions;
}

class RoadStatusLayer extends L.GeoJSON {
    _sectionLayers: { [key: string]: L.Polyline };
    // Raw geometry features keyed by id. The polylines in _sectionLayers are
    // built lazily from these the first time a feature becomes visible at the
    // current zoom, so segments filtered out by the zoom tier are never
    // materialized as Leaflet layers.
    _features: { [key: string]: Feature<LineString | MultiLineString> };
    _timer: NodeJS.Timer;
    _lastReceivedStatus: StatusData[];
    _offsettedLatLngs = {};
    // Zoom-independent projected (CRS-unit) points per feature id. The expensive
    // proj4 forward projection only depends on the geometry, not the zoom, so it
    // is computed once here; the per-zoom offset recompute then only re-applies
    // cheap linear scaling instead of re-projecting through proj4 every zoom.
    _projectedPoints: { [key: string]: L.Point[][] } = {};

    constructor(options: Partial<RoadStatusLayerOptions>) {
        super(undefined, { ...defaultOptions, ...options });
    }

    beforeAdd(map: L.Map): this {
        if (!this.sectionsLoaded()) {
            map.fire('beforelayeradd', { layer: this });
        }

        return this;
    }

    onAdd(map: L.Map): this {
        this._map = map;
        if (!this.sectionsLoaded()) {
            this.fire('data:loading');
        }

        this.options.source.subscribe(this.onSourceUpdate);
        this.options.statusSource.subscribe(this.onStatusUpdate);

        return super.onAdd(map);
    }

    onRemove(map: L.Map): this {
        if (this.sectionsLoaded()) {
            this.off('dblclick', this.onDoubleClick, this);

            map.off('zoomend', this.updateOffset, this);
            map.off('zoomend', this.setVisibleLayers, this);
        }

        this.options.source.unsubscribe(this.onSourceUpdate);
        this.options.statusSource.unsubscribe(this.onStatusUpdate);

        delete this._map;

        return super.onRemove(map);
    }

    onSourceUpdate = (features: Feature<LineString | MultiLineString>[]): void => {
        if (!this._map) {
            return;
        }

        // Keep only the raw geometry here; the actual polylines are created on
        // demand in setVisibleLayers/ensureLayer once a feature passes the
        // current zoom filter. This avoids constructing a Leaflet layer (and
        // tooltip) for every segment up front, which is the dominant cost for
        // the segment-grained v2 layer.
        this.clearLayers();
        this._sectionLayers = {};
        // Geometry may have changed; drop the derived projection/offset caches so
        // they are recomputed from the new shapes.
        this._projectedPoints = {};
        this._offsettedLatLngs = {};
        this._features = features.reduce((result, current) => {
            current.properties = current.properties || {};
            result[current.id] = current;
            return result;
        }, {});

        this.onLoaded();

        this.updateStatus(true);
    };

    // Lazily build (and cache) the polyline for a feature, applying any status
    // styling/tooltip it already carries. Layers are reused once created.
    ensureLayer(feature: Feature<LineString | MultiLineString>): L.Polyline {
        const existing = this._sectionLayers[feature.id];
        if (existing) {
            return existing;
        }

        const layer = L.GeoJSON.geometryToLayer(feature, this.options) as L.Polyline;
        layer.feature = feature;
        layer.setStyle(this.options.style as L.PathOptions);

        const status = feature.properties.status;
        if (status !== undefined) {
            layer.setStyle({
                color: this.options.color[status],
                opacity: 1,
                weight: this.getWeight(feature),
            });
        }

        if (this.options.tooltipTemplate) {
            // Render once here only to decide whether the segment is interactive
            // (non-green segments get a tooltip). The tooltip itself is bound as a
            // function so its markup is rendered lazily on hover rather than on
            // every status refresh.
            const hasTooltip = !!this.options.tooltipTemplate(feature.properties);
            // Disable pointer/hover hit-testing for segments without a tooltip
            // (e.g. green freeflow) so users don't think they're clickable.
            layer.options.interactive = hasTooltip;
            if (hasTooltip) {
                layer.bindTooltip(() => this.options.tooltipTemplate(feature.properties), { direction: 'auto', sticky: true });
            }
        }

        this._sectionLayers[feature.id] = layer;
        return layer;
    }

    onStatusUpdate = (data: StatusData[]): void => {
        this._lastReceivedStatus = data;
        this.updateStatus();
    };

    getOffsettedLatLngs(feature: Feature<LineString | MultiLineString>, offset: number, zoom: number): L.LatLng[][] {
        const cacheKey = `${feature.id.toString()}_${offset}_${zoom}`; // Cache individually for every zoom level
        const offsettedLatLngs = this._offsettedLatLngs[cacheKey];
        if (offsettedLatLngs) {
            return offsettedLatLngs;
        }

        const crs = this._map.options.crs as ProjectingCrs;
        const scale = crs.scale(zoom);

        // The expensive proj4 forward projection is zoom-independent and cached
        // per feature (getProjectedPoints). Per zoom we only scale those cached
        // points into pixel space (cheap linear transform - the same primitive
        // map.project uses internally), then simplify and offset. The proj4
        // inverse (unproject) still runs per zoom because the offset moves the
        // points to new positions.
        const latLngs = this.unprojectPoints(
            this.getProjectedPoints(feature).map((points) => {
                const scaled = points.map((point) => crs.transformation.transform(point, scale));
                const simplifiedLine = L.LineUtil.simplify(scaled, 0.2).map((point) => [point.x, point.y]);
                return offsetCoordinates(simplifiedLine, offset).map((coordinate) => L.point(coordinate as [number, number]));
            }),
            zoom,
        );

        this._offsettedLatLngs[cacheKey] = latLngs;
        return latLngs;
    }

    updateStatus(forceUpdate = false): void {
        if (this.sectionsLoaded() && this._lastReceivedStatus) {
            if (this.options.missingStatusMeansFreeflow) {
                const updateDate = this._lastReceivedStatus[0]?.updateDate ?? new Date().toISOString();
                Object.keys(this._features).forEach((id) => {
                    this.setStatus({ id, status: 'freeflow', updateDate });
                });
            } else {
                const statusIds = new Set(this._lastReceivedStatus.map((i) => i.id));
                // Drop geometry (and any built layer) for sections no longer present
                // in the status feed.
                Object.keys(this._features).forEach((id) => {
                    if (!statusIds.has(id)) {
                        const layer = this._sectionLayers[id];
                        if (layer) {
                            this._map.removeLayer(layer);
                            delete this._sectionLayers[id];
                        }
                        delete this._features[id];
                    }
                });
            }
            this._lastReceivedStatus.forEach((section) => {
                this.setStatus(section);
            });

            this.setVisibleLayers();
            this.fire('data:loaded');
        } else if (forceUpdate) {
            this.setVisibleLayers();
            this.fire('data:loaded');
        }
    }

    sectionsLoaded(): boolean {
        return !!this._features;
    }

    getWeight(feature: Feature): number {
        return this.options.weightByZoom ? this.options.weightByZoom(feature, this._map.getZoom()) : 2.5;
    }

    onLoaded(): void {
        this._map.on('zoomend', this.setVisibleLayers, this);
        if (this.options.offsetBy) {
            this._map.on('zoomend', this.updateOffset, this);
        }

        // Zoom map if doubleclick on line
        this.on('dblclick', this.onDoubleClick, this);
    }

    onDoubleClick(e: L.LeafletMouseEvent): void {
        e.containerPoint = this._map.mouseEventToContainerPoint(e.originalEvent);
        this._map.fire('dblclick', e);
    }

    setStatus(data: StatusData): void {
        const feature = this._features[data.id];
        if (!feature) {
            return;
        }

        const status = this.options.getStatus(data);
        const layer = this._sectionLayers[data.id];
        const statusChanged = status !== feature.properties.status;

        // Restyle in place. Draw order (more-severe on top) is re-established in
        // a single batched pass in setVisibleLayers, so we avoid the previous
        // remove/re-add churn that triggered extra canvas work per segment on
        // every status refresh.
        if (layer && statusChanged) {
            layer.setStyle({
                color: this.options.color[status],
                opacity: 1,
                weight: this.getWeight(feature),
            });
        }

        Object.assign(feature.properties, data, { status });

        // A segment's tooltip presence/interactivity only changes when its status
        // crosses the green/non-green boundary, so only re-evaluate it on status
        // change. The tooltip content is a function (bound here or in ensureLayer)
        // rendered lazily on hover, so an unchanged segment - the common case on
        // every refresh - does no tooltip work at all.
        if (layer && this.options.tooltipTemplate && statusChanged) {
            const hasTooltip = !!this.options.tooltipTemplate(feature.properties);
            layer.options.interactive = hasTooltip;
            if (hasTooltip) {
                if (!layer.getTooltip()) {
                    layer.bindTooltip(() => this.options.tooltipTemplate(feature.properties), { direction: 'auto', sticky: true });
                }
            } else if (layer.getTooltip()) {
                layer.unbindTooltip();
            }
        }

        if (layer?.isTooltipOpen() && this.options.tooltipTemplate) {
            layer.getTooltip().setContent(() => this.options.tooltipTemplate(feature.properties));
        }
    }

    setVisibleLayers(): void {
        if (!this._features) return;

        const zoom = this._map.getZoom();
        const featuresToShow = Object.values(this._features)
            .filter((f) => {
                return (
                    (!this.options.filterByZoom || this.options.filterByZoom(f, zoom)) &&
                    (!this.options.filterByStatus || this.options.filterByStatus(f))
                );
            })
            .sort((a, b) => this.options.getStatusCode(a.properties.status) - this.options.getStatusCode(b.properties.status));
        const visibleIds = new Set(featuresToShow.map((f) => String(f.id)));

        // Hide any already-built layer that is no longer visible at this zoom.
        Object.keys(this._sectionLayers).forEach((id) => {
            const layer = this._sectionLayers[id];
            if (!visibleIds.has(id) && this.hasLayer(layer)) {
                this.removeLayer(layer);
            }
        });

        // Build (lazily) and show the currently-visible features.
        featuresToShow.forEach((f) => {
            const layer = this.ensureLayer(f);
            if (this.options.offsetBy) {
                layer.setLatLngs(this.getOffsettedLatLngs(f, this.options.offsetBy, zoom));
            }
            if (this.options.weightByZoom) {
                layer.setStyle({ weight: this.options.weightByZoom(f, zoom) });
            }
            if (!this.hasLayer(layer)) {
                this.addLayer(layer);
            }
        });

        // Re-establish draw order in a single batched pass. featuresToShow is
        // sorted ascending by severity, so bringing each visible layer to the
        // front in that order leaves the most severe on top. Leaflet's canvas
        // renderer coalesces these into one redraw, replacing the per-segment
        // remove/add reordering that previously happened in setStatus.
        featuresToShow.forEach((f) => {
            const layer = this._sectionLayers[f.id];
            if (layer && this.hasLayer(layer)) {
                layer.bringToFront();
            }
        });
    }

    updateOffset(): void {
        const currentLayers = this.getLayers() as L.Polyline[];
        currentLayers.forEach((l) => l.setLatLngs(this.getOffsettedLatLngs(l.feature, this.options.offsetBy, this._map.getZoom())));
    }

    // Project a feature's geometry to CRS-unit points (zoom-independent) once and
    // cache it by feature id. This isolates the expensive proj4 forward transform
    // from the per-zoom offset recompute, which previously re-ran it every zoom.
    getProjectedPoints(feature: Feature<LineString | MultiLineString>): L.Point[][] {
        const cached = this._projectedPoints[feature.id];
        if (cached) {
            return cached;
        }

        const projection = (this._map.options.crs as ProjectingCrs).projection;
        const latLngs = L.GeoJSON.coordsToLatLngs(feature.geometry.coordinates, feature.geometry.type === 'LineString' ? 0 : 1);
        const projected = this.flattenLatLngs(latLngs).map((part) => part.map((latlng) => projection.project(latlng)));

        this._projectedPoints[feature.id] = projected;
        return projected;
    }

    // Flatten the nested latlng arrays produced by coordsToLatLngs into a flat
    // list of polyline parts (one entry per line in a Multi*).
    flattenLatLngs(latlngs: L.LatLng[] | L.LatLng[][] | L.LatLng[][][]): L.LatLng[][] {
        if (latlngs[0] instanceof L.LatLng) {
            return [latlngs as L.LatLng[]];
        }

        const result: L.LatLng[][] = [];
        for (const item of latlngs as (L.LatLng[] | L.LatLng[][])[]) {
            result.push(...this.flattenLatLngs(item));
        }
        return result;
    }

    unprojectPoints(points: L.Point[][], zoom: number): L.LatLng[][] {
        const result = [];
        points.forEach((p1) => {
            result.push(p1.map((p) => this._map.unproject(p, zoom)));
        });
        return result;
    }
}
export { RoadStatusLayer };
